import { Fragment, JSX } from 'react';

export interface ErrorCopyEntry {
  reason?: string;
  message?: string;
  compliance_message?: string;
}

interface ErrorCopySource {
  error_detail?: { error_copies?: ErrorCopyEntry[] } | null;
  error_message?: string | null;
}

/**
 * The ordered list of error-copy messages to show. Prefers the per-reason
 * array (`error_detail.error_copies`, one entry per reason — e.g. the earlier
 * failure and the duplicate), falling back to the single `error_message`.
 */
export function errorCopyMessages(file: ErrorCopySource): string[] {
  const copies = file.error_detail?.error_copies;
  if (Array.isArray(copies) && copies.length > 0) {
    return copies
      .map((copy) => (copy?.message ?? '').trim())
      .filter((message) => message.length > 0);
  }
  const single = file.error_message?.trim();
  return single ? [single] : [];
}

/**
 * Renders a stored error-copy string. The only markup understood is
 * `**bold**` spans and `\n` line breaks. Everything is emitted as React text
 * nodes (never dangerouslySetInnerHTML), so embedded HTML cannot execute.
 */
export function renderErrorCopy(text: string | null | undefined): JSX.Element {
  const value = text ?? '';
  const lines = value.split('\n');
  return (
    <>
      {lines.map((line, lineIndex) => (
        <Fragment key={lineIndex}>
          {lineIndex > 0 ? <br /> : null}
          {renderBold(line)}
        </Fragment>
      ))}
    </>
  );
}

function renderBold(line: string): JSX.Element[] {
  const parts = line.split('**');
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <strong key={index}>{part}</strong>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    )
  );
}

/**
 * The ordered list of COMPLIANCE-facing messages (staff audience): prefers each
 * entry's `compliance_message`, falling back to the renter message.
 */
export function complianceCopyMessages(file: ErrorCopySource): string[] {
  const copies = file.error_detail?.error_copies;
  if (Array.isArray(copies) && copies.length > 0) {
    const messages = copies
      .map((copy) => (copy?.compliance_message ?? copy?.message ?? '').trim())
      .filter((message) => message.length > 0);
    if (messages.length > 0) {
      return messages;
    }
  }
  return errorCopyMessages(file);
}

/**
 * Layout for a failure: one headline (the ORIGINAL error's, i.e. the first
 * entry), then the body — the nested/duplicate copies first, then the original
 * error's own body. Avoids repeating an "Upload a ..." headline per reason.
 */
/**
 * Split a message into a headline line + body. Uses the first newline when
 * present; otherwise falls back to the first sentence so single-line messages
 * (common for the compliance copy) still render a short headline.
 */
function splitHeadline(message: string): { headline: string; body: string } {
  const newline = message.indexOf('\n');
  if (newline >= 0) {
    return {
      headline: message.slice(0, newline),
      body: message.slice(newline + 1),
    };
  }
  const match = message.match(/^(.*?[.!?])\s+([\s\S]*)$/);
  if (match) {
    return { headline: match[1], body: match[2] };
  }
  return { headline: message, body: '' };
}

function layoutFromMessages(
  messages: string[],
  copies: ErrorCopyEntry[] | undefined
): { headline: string; body: string } {
  if (messages.length === 0) {
    return { headline: '', body: '' };
  }
  // The ORIGINAL error (any reason other than duplicate_file) drives the
  // headline; the duplicate is nested and contributes its body only, so its
  // near-identical "Upload a ..." headline is never repeated.
  let originalIndex = 0;
  if (Array.isArray(copies)) {
    const nonDuplicate = copies.findIndex(
      (copy) => copy?.reason && copy.reason !== 'duplicate_file'
    );
    if (nonDuplicate >= 0) {
      originalIndex = nonDuplicate;
    }
  }
  const original = splitHeadline(messages[originalIndex]);
  const others = messages.filter((_, index) => index !== originalIndex);
  const body = [
    ...others.map((message) => splitHeadline(message).body),
    original.body,
  ]
    .map((part) => part.trim())
    .filter(Boolean)
    .join('\n\n');
  return { headline: original.headline, body };
}

export function errorCopyLayout(file: ErrorCopySource): {
  headline: string;
  body: string;
} {
  return layoutFromMessages(
    errorCopyMessages(file),
    file.error_detail?.error_copies
  );
}

/**
 * The copy BODY only - the first line/sentence of each copy is its header and
 * is intentionally dropped. Falls back to the header when a copy has no body
 * (single-line messages), so a real message is never lost.
 */
export function errorCopyBody(file: ErrorCopySource): string {
  const { headline, body } = errorCopyLayout(file);
  return body || headline;
}

export function complianceCopyLayout(file: ErrorCopySource): {
  headline: string;
  body: string;
} {
  return layoutFromMessages(
    complianceCopyMessages(file),
    file.error_detail?.error_copies
  );
}

/**
 * Renders every error-copy message for a file as stacked blocks (one per
 * reason), falling back to `fallback` when there are none.
 */
export function ErrorCopyText({
  file,
  fallback,
}: {
  file: ErrorCopySource;
  fallback?: string;
}): JSX.Element {
  const messages = errorCopyMessages(file);
  const list = messages.length ? messages : fallback ? [fallback] : [];
  return (
    <>
      {list.map((message, index) => (
        <span
          key={index}
          style={{ display: 'block', marginTop: index ? 8 : 0 }}
        >
          {renderErrorCopy(message)}
        </span>
      ))}
    </>
  );
}
